import React, { useEffect, useState, Component } from 'react';
import { UsersIcon, PlusIcon, EditIcon, Trash2Icon, ShieldIcon, UserIcon, EyeIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { Select } from '../components/Select';
import { useFirestoreCollection } from '../hooks/useFirestore';
export type UserRole = 'Admin' | 'Medarbejder' | 'Read-Only';
export interface User {
  id?: string;
  email: string;
  password: string;
  role: UserRole;
}
interface UsersProps {
  currentUserEmail: string;
}
export function Users({
  currentUserEmail
}: UsersProps) {
  const {
    data: users,
    add: addUser,
    update: updateUser,
    remove: removeUser
  } = useFirestoreCollection<User>('users');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const handleAddUser = async (email: string, password: string, role: UserRole) => {
    try {
      await addUser({
        email,
        password,
        role
      });
      setIsAddModalOpen(false);
    } catch (error) {
      console.error('Error adding user:', error);
      alert('Fejl ved tilføjelse af bruger');
    }
  };
  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setIsEditModalOpen(true);
  };
  const handleSaveUser = async (updatedUser: User) => {
    try {
      if (!updatedUser.id) return;
      await updateUser(updatedUser.id, {
        email: updatedUser.email,
        password: updatedUser.password,
        role: updatedUser.role
      });
      // Update current user's password in localStorage if editing self
      if (updatedUser.email === currentUserEmail) {
        localStorage.setItem('password', updatedUser.password);
      }
      setIsEditModalOpen(false);
      setEditingUser(null);
    } catch (error) {
      console.error('Error updating user:', error);
      alert('Fejl ved opdatering af bruger');
    }
  };
  const handleDeleteUser = async (user: User) => {
    // CRITICAL: Prevent deletion of default admin
    if (user.email === 'daniel@lykke-el.dk') {
      alert('Standard admin brugeren kan ikke slettes');
      return;
    }
    if (user.email === currentUserEmail) {
      alert('Du kan ikke slette din egen bruger');
      return;
    }
    if (!window.confirm(`Er du sikker på at du vil slette brugeren "${user.email}"?`)) {
      return;
    }
    try {
      if (user.id) {
        await removeUser(user.id);
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Fejl ved sletning af bruger');
    }
  };
  const getRoleBadgeColor = (role: UserRole) => {
    switch (role) {
      case 'Admin':
        return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'Medarbejder':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'Read-Only':
        return 'bg-stone-100 text-stone-700 border-stone-200';
      default:
        return 'bg-stone-100 text-stone-700 border-stone-200';
    }
  };
  const getRoleIcon = (role: UserRole) => {
    switch (role) {
      case 'Admin':
        return <ShieldIcon className="w-6 h-6 text-purple-700" data-id="element-1743" data-mp="4h5n6gfai6xr" />;
      case 'Medarbejder':
        return <UserIcon className="w-6 h-6 text-blue-700" data-id="element-1744" data-mp="4iyuzz1n2x7c" />;
      case 'Read-Only':
        return <EyeIcon className="w-6 h-6 text-stone-700" data-id="element-1745" data-mp="3mttyb32rwynt" />;
      default:
        return <UserIcon className="w-6 h-6 text-stone-700" data-id="element-1746" data-mp="3rmn4zwhsyh2j" />;
    }
  };
  const getRoleBackgroundColor = (role: UserRole) => {
    switch (role) {
      case 'Admin':
        return 'bg-purple-100';
      case 'Medarbejder':
        return 'bg-blue-100';
      case 'Read-Only':
        return 'bg-stone-100';
      default:
        return 'bg-stone-100';
    }
  };
  return <div data-id="element-1747" data-mp="37xmfa633q3a4">
      <header className="mb-8" data-id="element-1748" data-mp="37y4b51luqfhi">
        <h1 className="text-4xl font-bold text-stone-900 mb-2" data-id="element-1749" data-mp="37xcserdcsc8n">Brugere</h1>
        <p className="text-stone-600" data-id="element-1750" data-mp="30tmoyd18yri1">Administrer brugerkonti og roller</p>
      </header>

      <div className="mb-6" data-id="element-1751" data-mp="2vvbeq171ieza">
        <Button onClick={() => setIsAddModalOpen(true)} className="flex items-center gap-2" data-id="element-1752" data-mp="2vw3cnio40osq">
          <PlusIcon className="w-4 h-4" data-id="element-1753" data-mp="2r2s1548crjuh" />
          Tilføj bruger
        </Button>
      </div>

      <div className="grid gap-4" data-id="element-1754" data-mp="2m3p86i5nd42v">
        {users.map((user, index) => <motion.div key={user.id} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: index * 0.05
      }} className="bg-white rounded-xl p-6 border border-stone-200 shadow-sm" data-id="element-1755" data-mp="2i4yao9x9rkf8" data-mpi={index}>
            <div className="flex items-center justify-between" data-id="element-1756" data-mp="6dktujzrhyp0">
              <div className="flex items-center gap-4" data-id="element-1757" data-mp="1fsqmtto2q5i">
                <div className={`p-3 rounded-lg ${getRoleBackgroundColor(user.role)}`} data-id="element-1758" data-mp="1gc8ybn8bgk2">
                  {getRoleIcon(user.role)}
                </div>
                <div data-id="element-1759" data-mp="dwlpkj4bmo1t">
                  <h3 className="text-lg font-semibold text-stone-900" data-id="element-1760" data-mp="dw24wunadlk3">
                    {user.email}
                  </h3>
                  <div className="flex items-center gap-2 mt-1" data-id="element-1761" data-mp="nnorfn52r725">
                    <span className={`px-3 py-1 text-xs font-medium rounded-full border ${getRoleBadgeColor(user.role)}`} data-id="element-1762" data-mp="nq2jdn4gk35h">
                      {user.role}
                    </span>
                    {user.email === currentUserEmail && <span className="px-3 py-1 text-xs font-medium rounded-full border bg-teal-100 text-teal-700 border-teal-200" data-id="element-1763" data-mp="3w26ufag5rwew">
                        Dig
                      </span>}
                    {user.email === 'daniel@lykke-el.dk' && <span className="px-3 py-1 text-xs font-medium rounded-full border bg-purple-100 text-purple-700 border-purple-200" data-id="element-1764" data-mp="1gbklsl768mvr">
                        Standard Admin
                      </span>}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2" data-id="element-1765" data-mp="10mxnvxhh0g6v">
                <button onClick={() => handleEditUser(user)} className="p-2 text-stone-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-all" aria-label="Rediger bruger" data-id="element-1766" data-mp="10kbertexlsmj">
                  <EditIcon className="w-5 h-5" data-id="element-1767" data-mp="308cwy4sit8v" />
                </button>
                {user.email !== currentUserEmail && user.email !== 'daniel@lykke-el.dk' && <button onClick={() => handleDeleteUser(user)} className="p-2 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all" aria-label="Slet bruger" data-id="element-1768" data-mp="cw1yf70hlx2s">
                      <Trash2Icon className="w-5 h-5" data-id="element-1769" data-mp="1vw17e0iwvpch" />
                    </button>}
              </div>
            </div>
          </motion.div>)}
      </div>

      {/* Add User Modal */}
      <AddUserModal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} onAdd={handleAddUser} existingEmails={users.map(u => u.email)} data-id="element-1770" data-mp="1ly3j5d11xsjv" />

      {/* Edit User Modal */}
      <EditUserModal isOpen={isEditModalOpen} onClose={() => {
      setIsEditModalOpen(false);
      setEditingUser(null);
    }} onSave={handleSaveUser} user={editingUser} existingEmails={users.map(u => u.email)} isCurrentUser={editingUser?.email === currentUserEmail} data-id="element-1771" data-mp="2enafq6gy482h" />
    </div>;
}
// Add User Modal Component
interface AddUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (email: string, password: string, role: UserRole) => void;
  existingEmails: string[];
}
function AddUserModal({
  isOpen,
  onClose,
  onAdd,
  existingEmails
}: AddUserModalProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<UserRole>('Medarbejder');
  const [error, setError] = useState('');
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!email || !password) {
      setError('Udfyld venligst alle felter');
      return;
    }
    if (existingEmails.includes(email)) {
      setError('En bruger med denne email eksisterer allerede');
      return;
    }
    if (password.length < 6) {
      setError('Adgangskode skal være mindst 6 tegn');
      return;
    }
    onAdd(email, password, role);
    setEmail('');
    setPassword('');
    setRole('Medarbejder');
    setError('');
  };
  if (!isOpen) return null;
  return <AnimatePresence data-id="element-1772" data-mp="2saufee742hjx">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40" onClick={onClose} data-id="element-1773" data-mp="2sbdm0yrb9jin" />
      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-1774" data-mp="2zud3gyv7p0zd">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" data-id="element-1775" data-mp="3irwdo5teopnd">
          <h2 className="text-2xl font-semibold text-stone-900 mb-6" data-id="element-1776" data-mp="3nj61d0ve9iwm">
            Tilføj bruger
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4" data-id="element-1777" data-mp="3shzhl122fo0h">
            <Input label="Email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="bruger@email.dk" required data-id="element-1778" data-mp="3skbwf5cj1i9b" />

            <Input label="Adgangskode" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Mindst 6 tegn" required data-id="element-1779" data-mp="1s70x72ndjwz2" />

            <Select label="Rolle" value={role} onChange={e => setRole(e.target.value as UserRole)} options={[{
            value: 'Admin',
            label: 'Admin'
          }, {
            value: 'Medarbejder',
            label: 'Medarbejder'
          }, {
            value: 'Read-Only',
            label: 'Read-Only'
          }]} data-id="element-1780" data-mp="223dndqjpnsp2" />

            {error && <div className="bg-red-50 border border-red-200 rounded-lg p-3" data-id="element-1781" data-mp="qi6ytmaua9mv">
                <p className="text-sm text-red-700" data-id="element-1782" data-mp="vfqaqfk2eabn">{error}</p>
              </div>}

            <div className="flex gap-3 pt-2" data-id="element-1783" data-mp="38q9p3pvc9qor">
              <Button type="button" variant="secondary" onClick={onClose} className="flex-1" data-id="element-1784" data-mp="38s2t0xekhwt2">
                Annuller
              </Button>
              <Button type="submit" variant="primary" className="flex-1" data-id="element-1785" data-mp="2zw3it43t91bk">
                Tilføj bruger
              </Button>
            </div>
          </form>
        </div>
      </motion.div>
    </AnimatePresence>;
}
// Edit User Modal Component
interface EditUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (user: User) => void;
  user: User | null;
  existingEmails: string[];
  isCurrentUser: boolean;
}
function EditUserModal({
  isOpen,
  onClose,
  onSave,
  user,
  existingEmails,
  isCurrentUser
}: EditUserModalProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<UserRole>('Medarbejder');
  const [error, setError] = useState('');
  useEffect(() => {
    if (user) {
      setEmail(user.email);
      setPassword(user.password);
      setRole(user.role);
    }
  }, [user]);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!user || !email || !password) {
      setError('Udfyld venligst alle felter');
      return;
    }
    if (email !== user.email && existingEmails.includes(email)) {
      setError('En bruger med denne email eksisterer allerede');
      return;
    }
    if (password.length < 6) {
      setError('Adgangskode skal være mindst 6 tegn');
      return;
    }
    onSave({
      ...user,
      email,
      password,
      role
    });
    setError('');
  };
  if (!isOpen || !user) return null;
  return <AnimatePresence data-id="element-1786" data-mp="1g2tuauoruxyf">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40" onClick={onClose} data-id="element-1787" data-mp="1fzxuf0td5z7c" />
      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-1788" data-mp="ngby8gwbbac5">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" data-id="element-1789" data-mp="16dwpfumjukzx">
          <h2 className="text-2xl font-semibold text-stone-900 mb-6" data-id="element-1790" data-mp="11fbo1rvk9v9w">
            Rediger bruger
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4" data-id="element-1791" data-mp="1g4ra4uk5dcj9">
            <Input label="Email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="bruger@email.dk" required disabled={isCurrentUser} data-id="element-1792" data-mp="1g6anodef59hc" />

            <Input label="Adgangskode" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Mindst 6 tegn" required data-id="element-1793" data-mp="1l4voof1hvx6u" />

            <Select label="Rolle" value={role} onChange={e => setRole(e.target.value as UserRole)} options={[{
            value: 'Admin',
            label: 'Admin'
          }, {
            value: 'Medarbejder',
            label: 'Medarbejder'
          }, {
            value: 'Read-Only',
            label: 'Read-Only'
          }]} disabled={isCurrentUser} data-id="element-1794" data-mp="3obb9n9k36y4p" />

            {isCurrentUser && <div className="bg-blue-50 border border-blue-200 rounded-lg p-3" data-id="element-1795" data-mp="kvw2cg33f2n9">
                <p className="text-sm text-blue-700" data-id="element-1796" data-mp="owfzvkrkw89x">
                  Du kan ikke ændre din egen email eller rolle
                </p>
              </div>}

            {error && <div className="bg-red-50 border border-red-200 rounded-lg p-3" data-id="element-1797" data-mp="25df0frsmadcx">
                <p className="text-sm text-red-700" data-id="element-1798" data-mp="25fj1m1z4sy9i">{error}</p>
              </div>}

            <div className="flex gap-3 pt-2" data-id="element-1799" data-mp="20k3ppfkopcfq">
              <Button type="button" variant="secondary" onClick={onClose} className="flex-1" data-id="element-1800" data-mp="1vo54g0unkajj">
                Annuller
              </Button>
              <Button type="submit" variant="primary" className="flex-1" data-id="element-1801" data-mp="1lnmf4qkbk1u8">
                Gem ændringer
              </Button>
            </div>
          </form>
        </div>
      </motion.div>
    </AnimatePresence>;
}